<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="4" />
      <vs-col vs-w="2">
        <select-with-multiple
          v-model="filters.owners"
          class="px-2"
          :options="options.owners"
          placeholder="Select Owners"
          :activate-search="true"
        />
      </vs-col>
      <vs-col vs-w="2">
        <div class="flex items-center">
          <date-picker
            v-model="filters.start_date"
            type="date"
            value-type="YYYY-MM-DD"
            format="DD MMM YYYY"
            placeholder="Expiry Date"
            :disabled="false"
          />
        </div>
      </vs-col>
      <vs-col vs-w="2">
        <div class="flex items-center">
          <date-picker
            v-model="filters.end_date"
            type="date"
            value-type="YYYY-MM-DD"
            format="DD MMM YYYY"
            placeholder="Expiry Date"
            :min="dateDisabledBefore"
            :max="dateFilterDisabledAfter"
            :disabled="false"
          />
        </div>
      </vs-col>
      <vs-col vs-w="1">
        <div class="flex items-center pl-2">
          <vs-button
            type="border"
            color="primary"
            class="px-2 w-40 self-center font-semibold"
            @click="resetFilters"
          >
            Reset
          </vs-button>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchDockingSchedules"
        ref="tabletop"
      >
        <template #start>
          <div
            class="flex items-center justify-center"
          >
            <vs-button
              v-show="isUserCanAccessMenu(permission.name, 'add')"
              :class="`button w-max font-semibold ${isBusy && 'cursor-not-allowed opacity-50 animate-bounce'}`"
              color="primary"
              :disabled="isBusy"
              @click="startAdd"
            >
              Add New Schedule
            </vs-button>
            <vs-button
              :class="`button w-min font-semibold ml-5 ${isBusy && 'cursor-not-allowed opacity-50 animate-bounce'}`"
              color="secondary"
              :disabled="isBusy"
              @click="isDownload = true"
            >
              Export
            </vs-button>
            <vs-button
              :class="`button w-min font-semibold ml-5 ${isBusy && 'cursor-not-allowed opacity-50 animate-bounce'}`"
              color="secondary"
              :disabled="isBusy"
              @click="sendDockingScheduleEmail"
            >
              Send Email
            </vs-button>
          </div>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :table-header="dockingScheduleHeaders"
        :table-data="dockingScheduleData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :loading="isBusy"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click=" isProcessing ? null : saveEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-check-circle.svg')">
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click=" isProcessing ? null : cancelEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-close-circle.svg')">
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-edit.svg')">
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startDelete(row)"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
            </template>
          </div>
        </template>
        <template #owner="{ row }">
          <template v-if="row.isEditing">
            <div
              class="relative"
              style="min-width: 18rem;"
            >
              <div>
                <select-with-search
                  :disabled="isProcessing"
                  v-model="row.editForm.owner_id.value"
                  :class="{ 'is-not-valid': !row.editForm.owner_id.isValid }"
                  :options="options.owners"
                  placeholder="Tug Owner"
                />
              </div>
              <span
                v-if="!row.editForm.owner_id.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.owner || '-' }}
          </template>
        </template>
        <template #category="{ row }">
          <div v-if="row.isEditing">
            <div
              class="relative"
              style="min-width: calc(24 * 4px)"
            >
              <template>
                <select-with-search
                  :disabled="isProcessing"
                  v-model="row.editForm.category.value"
                  :class="{ 'is-not-valid': !row.editForm.category.isValid }"
                  :options="options.categories"
                  placeholder="Category"
                />
              </template>
              <span
                v-if="!row.editForm.category.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </div>
          <div
            v-else
            style="min-width: calc(24 * 4px)"
            :class="`w-24 px-2 badge-category ${row.category === 'TB' ? 'tug' : 'barge'}`"
          >
            <span>
              {{ row.category || '-' }}
            </span>
          </div>
        </template>
        <template #name="{ row }">
          <template v-if="row.isEditing">
            <div
              class="relative"
              style="min-width: calc(40 * 4px)"
            >
              <div>
                <select-with-search
                  :disabled="isProcessing"
                  v-model="row.editForm.name.value"
                  :class="{ 'is-not-valid': !row.editForm.name.isValid }"
                  :options="options.tugsBarges"
                  :placeholder="row.editForm.category.value === 'BG' ? 'Name of Barge' : 'Name of Tug'"
                />
              </div>
              <span
                v-if="!row.editForm.name.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.name || '-' }}
          </template>
        </template>
        <template #schedule="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <date-picker
                  v-model="row.editForm.date.value"
                  type="date"
                  value-type="YYYY-MM-DD"
                  format="DD MMM YYYY"
                  :class="{ 'is-not-valid': !row.editForm.date.isValid }"
                  placeholder="Docking Schedule"
                  :disabled="isProcessing"
                />
              </div>
              <span
                v-if="!row.editForm.date.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ moment(row.date).utc().format('DD MMM YYYY') || '-' }}
          </template>
        </template>
        <template #status="{ row }">
          <template v-if="row.isEditing">
            <div class="relative w-56">
              <div>
                <select-with-search
                  :disabled="isProcessing"
                  v-model="row.editForm.status.value"
                  :class="{ 'is-not-valid': !row.editForm.status.isValid }"
                  :options="options.status"
                  placeholder="Docking Status"
                />
              </div>
              <span
                v-if="!row.editForm.status.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.status || '-' }}
          </template>
        </template>
        <template #remarks="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.remarks.value"
                  placeholder="Remarks"
                />
              </div>
            </div>
          </template>
          <template v-else>
            {{ row.remarks || '-' }}
          </template>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchDockingSchedules()"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete Docking Schedule"
      left-button="Cancel"
      right-button="Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null }"
      @leftButton="popUp = { isShown: false, tempId: null }"
      @rightButton="deleteDockingSchedule"
    >
      <p class="text-center mt-2">
        Are you sure you want to delete docking schedule for
      </p>
      <p class="text-center mt-2">
        "{{ popUp.tempId ? dockingScheduleData.find((item) => item.id === popUp.tempId).name : '' }}"?
      </p>
      <p class="text-center mt-2">
        This action cannot be undone
      </p>
    </pop-up>
  </div>
</template>

<script>
import moment from "moment";
import {mapGetters} from "vuex";

import DataTable from "@/components/dataTable";
import DatePicker from '@/components/datePicker'
import PopUp from "@/components/popUp";
import SelectWithMultiple from "@/components/selectWithMultiple";
import SelectWithSearch from "@/components/selectWithSearch";
import TableBottom from "@/components/tableBottom";
import TableTop from "@/components/tableTop";
import { PAGE_TITLE } from "@/fixtures/Constants";
import downloadUtils from "@/utils/downloadUtils";

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    PopUp,
    TableBottom,
    SelectWithSearch,
    DataTable,
    TableTop,
    SelectWithMultiple,
    DatePicker
  },
  data() {
    const defaultDockingScheduleDate = {
      from: moment().startOf('month').format('YYYY-MM-DD'),
      to: moment().add(2, 'month').endOf('month').format('YYYY-MM-DD')
    }
    return {
      isProcessing: false,
      isBusy: false,
      popUp: {
        isShown: false,
        tempId: null
      },
      popUpSuccess: false,
      dockingScheduleHeaders: [
        { key: 'no', label: 'No', sortable: false, width: '50px' },
        { key: 'owner', label: 'Owner', sortable: true },
        { key: 'category', label: 'Category', sortable: true },
        { key: 'name', label: 'Name of Tug or Barge', sortable: true },
        { key: 'schedule', label: 'Docking Schedule', width: '185px', sortable: true },
        { key: 'status', label: 'Docking Status', sortable: true },
        { key: 'remarks', label: 'Remarks', sortable: true }
      ],
      dockingScheduleData: [],
      filters: {
        sort_by: '',
        sort_dir: '',
        owners: [],
        start_date: defaultDockingScheduleDate.from,
        end_date: defaultDockingScheduleDate.to
      },
      options: {
        owners: [],
        categories: [
          {
            value: 'TB',
            text: 'Tugboat'
          },
          {
            value: 'BG',
            text: 'Barge'
          }],
        status: [],
        tugsBarges: []
      },
      dateDisabledAfter: new Date(new Date().getFullYear() + 1, new Date().getMonth() + 3, 1),
      defaultDockingScheduleDate: defaultDockingScheduleDate,
      isAdding: false,
      isDownload: false
    }
  },
  computed: {
    ...mapGetters({
      dockingSchedules: 'DockingSchedule/dockingSchedules',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      owners: 'DockingSchedule/owners',
      status: 'DockingSchedule/status',
      dockingSchedulesFiles: 'DockingSchedule/dockingSchedulesFiles',
      tugsBarges: 'DockingSchedule/tugsBarges'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    },
    dateDisabledBefore() {
      const dateFrom = this.filters.start_date
      return new Date(
        new Date(dateFrom).getFullYear(),
        new Date(dateFrom).getMonth(),
        new Date(dateFrom).getDate()
      )
    },
    dateFilterDisabledAfter() {
      const dateFrom = this.filters.start_date
      return new Date(
        new Date(dateFrom).getFullYear() + 7,
        new Date(dateFrom).getMonth(),
        new Date(dateFrom).getDate()
      )
    },
    currentEdit() {
      const editedRow = this.dockingScheduleData.find((schedule) => schedule.isEditing)
      if (editedRow) {
        return {
          owner_id: editedRow.editForm.owner_id.value,
          category: editedRow.editForm.category.value
        }
      }
      return { owner_id: null, category: null }
    },
    activeRow() {
      return this.dockingScheduleData.findIndex(schedule => schedule.isEditing)
    }
  },
  mounted() {
    if (this.$route.query.start_date || this.$route.query.end_date) {
      try {
        const startDate = this.$route.query.start_date
        const endDate = this.$route.query.end_date
        
        if (startDate && endDate) {
          const parsedStartDate = moment(startDate, 'YYYY-MM-DD')
          const parsedEndDate = moment(endDate, 'YYYY-MM-DD')
          
          if (parsedStartDate.isValid() && parsedEndDate.isValid()) {
            this.filters.start_date = parsedStartDate.format('YYYY-MM-DD')
            this.filters.end_date = parsedEndDate.format('YYYY-MM-DD')
          } else {
            this.showFailedNotification(this, 'Invalid date parameter format, please try again')
          }
        } else {
          this.showFailedNotification(this, 'Both start_date and end_date are required')
        }
      } catch (e) {
        this.showFailedNotification(this, 'Invalid date parameter format, please try again')
      }
    }
    this.fetchOwners()
    this.fetchCategories()
    this.fetchStatus()
    this.fetchDockingSchedules()

  },
  watch: {
    dockingSchedules: {
      handler(val) {
        if (val) {
          this.dockingScheduleData = val.map(item =>( {
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    owners: {
      handler(val) {
        if (val) {
          this.options.owners = val.map(item => ({
            value: item.id,
            text: item.name
          }))
        }
      }
    },
    status: {
      handler(val) {
        if (val) {
          this.options.status = val.map(item => ({
            value: item.id,
            text: item.name
          }))
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.dockingScheduleHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.dockingScheduleHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.dockingScheduleHeaders.splice(index, 1);
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchDockingSchedules()
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadDockingSchedules();
        }
      }
    },
    dockingSchedulesFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, 'Docking_schedules.xlsx')
          // Reset the companiesFiles state
          this.$store.dispatch('DockingSchedules/resetDockingSchedulesFiles');
        }
      }
    },
    tugsBarges: {
      handler(val) {
        if (val) {
          this.options.tugsBarges = val.map(item => ({
            value: item.id,
            text: item.name
          }))

          if (this.activeRow === -1) return;

          const rowData = this.dockingScheduleData[this.activeRow];
          const currentName = rowData?.name || null;

          if (!currentName) {
            rowData.editForm.name.value = null;
            return;
          }

          const currentValue = rowData.editForm.name.value;
          rowData.editForm.name.value =
            currentName === currentValue
              ? this.options.tugsBarges.find(item => item.text === currentValue)?.value ?? null
              : null;
        }
      }
    },
    currentEdit: {
      deep: true,
      handler(val) {
        if (val.owner_id && val.category) {
          const payload = {
            type: val.category === 'TB'? 'TUG' : 'BARGE',
            owner_id: val.owner_id
          }
          this.fetchTugsBarges(payload)
        }
      }
    }
  },
  methods: {
    async fetchOwners() {
      try {
        await this.$store.dispatch('DockingSchedule/fetchOwners')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch owner, please try again')
      }
    },
    async fetchCategories() {
      try {
        await this.$store.dispatch('DockingSchedule/fetchCategories')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to categories, please try again')
      }
    },
    async fetchStatus() {
      try {
        await this.$store.dispatch('DockingSchedule/fetchStatus')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch status, please try again')
      }
    },
    async fetchDockingSchedules() {
      try {
        this.isBusy = true
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key])
          .reduce((item, key) => {
            if (key === 'owners') {
              item.owner = this.filters.owners.map(owner => owner.value) || null
            } else {
              item[key] = this.filters[key]
            }
            return item
          }, {})
        await this.$store.dispatch(
          'DockingSchedule/fetchDockingSchedules',
          {
            ...apiFilters
          }
        )
        this.isAdding = false;
        this.isBusy = false
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch docking schedule list, please try again')
        this.isBusy = false
      }
    },
    async fetchTugsBarges(payload) {
      try {
        await this.$store.dispatch('DockingSchedule/fetchTugsBarges', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch owner, please try again')
      }
    },
    async deleteDockingSchedule(){
      try {
        await this.$store.dispatch('DockingSchedule/deleteDockingSchedule', this.popUp.tempId)
        this.popUp = { isShown: false, tempId: null }
        this.showSuccessNotification(this, 'Docking Schedule successfully deleted.')

        await this.fetchDockingSchedules()
      } catch (e) {
        this.showFailedNotification(
          this,
          'Failed to delete Docking Schedule , please try again',
          'Error'
        )
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchDockingSchedules()
      this.popUpSuccess = false
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir

      this.fetchDockingSchedules()
    },
    startAdd() {
      // Cancel any existing edit
      if (this.isAdding || this.dockingScheduleData.some(row => row.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      // Create new row
      const newRow = {
        isEditing: true,
        editForm: {
          name: { value: '', isValid: true },
          owner_id: { value: '', isValid: true },
          category: { value: '', isValid: true },
          date: { value: '', isValid: true },
          status: { value: '', isValid: true },
          remarks: { value: '' }
        }
      }

      this.isAdding = true
      this.dockingScheduleData.unshift(newRow)
    },
    startEdit(row) {
      // Cancel any existing edit or add
      if (this.isAdding || this.dockingScheduleData.some(r => r.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      const index = this.dockingScheduleData.findIndex(item => item.id === row.id)
      if (index !== -1) {
        this.$set(this.dockingScheduleData[index], 'isEditing', true)
        this.$set(this.dockingScheduleData[index], 'editForm', {
          name: { value: row.name, isValid: true },
          remarks: { value: row.remarks },
          owner_id: {
            value: row.owner
              ? this.options.owners.find(option => option.text === row.owner)?.value
              : '',
            isValid: true
          },
          category: {
            value: row.category
              ? this.options.categories.find(option => option.value === row.category)?.value
              : '',
            isValid: true
          },
          status: {
            value: row.status
              ? this.options.status.find(option => option.text === row.status)?.value
              : '',
            isValid: true
          },
          date: { value: row.date, isValid: true }
        })
      }
    },
    startDelete(row) {
      // Cancel any existing edit or add
      if (this.isAdding || this.dockingScheduleData.some(r => r.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      this.popUp = {
        isShown: true,
        tempId: row.id
      }
    },
    cancelAllEdits() {
      this.dockingScheduleData.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
          this.$set(row, 'editForm', null)
        }
      })
      this.isAdding = false
    },
    cancelEdit(row) {
      if (this.isAdding) {
        // Remove the new row if we're canceling an add
        const index = this.dockingScheduleData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.dockingScheduleData.splice(index, 1)
        }
      } else {
        const index = this.dockingScheduleData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.$set(this.dockingScheduleData[index], 'isEditing', false)
          this.$set(this.dockingScheduleData[index], 'editForm', null)
        }
      }
      this.isAdding = false
    },
    async saveEdit(row) {
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(
          this,
          'Some fields is empty, please fill it first',
          'Failed'
        )
        return
      }

      try {
        const formData = {
          ...Object.keys(row.editForm).reduce((item, key) => {
            if (key === 'status') {
              item[key] = this.options.status.find(option => option.value === row.editForm[key].value)?.text
            } else {
              item[key] = row.editForm[key].value
            }
            return item
          }, {})
        }

        if (formData['category'] === 'TB') formData['tug_id'] = formData['name']
        else formData['barge_id'] = formData['name']
        delete formData['name']
        this.isProcessing = true
        if (this.isAdding) {
          await this.$store.dispatch('DockingSchedule/postDockingSchedule', formData)
          this.showSuccessNotification(this, 'Docking Schedule successfully added.')
        } else {
          await this.$store.dispatch('DockingSchedule/putDockingSchedule', {
            id: row.id,
            form: formData
          })
          this.showSuccessNotification(this, 'Docking Schedule successfully updated.')
        }
        // Reset state
        this.cancelAllEdits()
        await this.fetchDockingSchedules()
        this.isProcessing = false
      } catch ({ error }) {
        this.isProcessing = false
        this.showFailedNotification(
          this,
          error?.message || 'Failed to save changes, please try again',
          'Error'
        )
      }
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },
    async sendDockingScheduleEmail() {
      this.isBusy = true
      try {
        await this.$store.dispatch('DockingSchedule/postDockingScheduleSendEmail',{
          ...(this.filters.owners.length ? { owners: this.filters.owners.map(owner => owner.value.toString()) } : null),
          ...(this.filters.start_date ? { start_date: this.filters.start_date } : null),
          ...(this.filters.end_date ? { end_date: this.filters.end_date } : null)
        })

        this.showSuccessNotification(this, 'Docking schedule email successfully sent')
      } catch ({ data }) {
        const message = data?.error?.message || 'Failed to send docking schedule email, please try again'
        this.showFailedNotification(this, message)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filters = {
        sort_by: '',
        sort_by_name: '',
        owners: [],
        start_date: this.defaultDockingScheduleDate.from,
        end_date: this.defaultDockingScheduleDate.to
      };
      this.$refs.tabletop.$emit('reset-search')
      this.fetchDockingSchedules()
    },
    async downloadDockingSchedules() {
      try {
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key])
          .reduce((item, key) => {
            if (key === 'owners') {
              item.owner = this.filters.owners.map(owner => owner.value) || null
            } else {
              item[key] = this.filters[key]
            }
            return item
          }, {})
        this.isBusy = true
        await this.$store.dispatch(
          'DockingSchedule/downloadDockingSchedules',
          apiFilters,
          {
            headers: {
              accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
        )
        this.isBusy = false
        this.isDownload = false;
      } catch (error) {
        this.isBusy = false
        this.isDownload = false
        this.showFailedNotification(this, 'Failed to download docking schedule, please try again');
      }
    }
  },
  beforeCreate() {
    this.$store.dispatch('Table/resetConfig')
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.edit-container {
  min-width: calc(40 * 4px);
}

.button {
  @apply py-4;
  @apply rounded-lg;
  @apply border-2;
  @apply font-semibold;
  @apply min-w-button;
  @apply text-base;
  @apply bg-primary-accent;
  height: calc(1rem * 10 / 3);

  &.shadow-secondary {
    -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
    box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  }

  &:focus {
    @apply outline-none;
  }
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.bg-contract {
  @apply py-1;
  @apply px-3;
  @apply font-semibold;
  @apply rounded-lg;

  &.new {
    @apply text-primary;
    background-color: #F1F1FD;
  }

  &.updated {
    @apply text-danger;
    @apply bg-danger-accent;
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}

.btn-download {
  background-color: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.badge-category {
  font-weight: 500;
  text-align: center;
  padding: 5px 0;
  border-radius: 5px;

  &.tug {
    background-color:rgba(76, 175, 80, 0.2);
    color: #4CAF50
  }

  &.barge {
    background-color: rgba(83, 130, 251, 0.2);
    color: #5382FB
  }
}
</style>